import {
  Popup,
  CircleMarker,
  Polygon,
  Polyline,
  Marker,
} from 'react-leaflet'
import MapPopupTable from '../MapPopupTable/MapPopupTable'
import '../CustomMapMarker/CustomMapMarker.css'
import NetworkData from '../../interfaces/networkData'
import { useSelector } from 'react-redux'
import { elements } from '../../interfaces/alertingelements'
import EventBox from '../Alerting/AlertingBox/EventBox'
import PolygonPopupTable from '../MapPopupTable/PolygonPopupTable'
import { RootState } from '../../store'
import { AirmetPolygon } from './PolygonMapMarker'
import TAFMarker, { METARMarker } from './TAFMETARMarker'
import {sensorMarkerIcon, alertMarkerIcon} from '../../assets/icons/sensorMarkerIcons'
import { useSensorEventContext } from '../../utils/SensorAlertsUtils'
import AlertsPopup from '../Alerting/AlertListBox/AlertsPopup'
import React from 'react'
import { WeatherThresholdConfig } from '../../interfaces/user'

const evaluateComparator = (value: number, comparator: string, threshold: number): boolean => {
  switch (comparator) {
    case '<':
      return value < threshold
    case '>':
      return value > threshold
    case '<=':
      return value <= threshold
    case '>=':
      return value >= threshold
    case '===':
      return value === threshold
    case '!==':
      return value !== threshold
    default:
      return false
  }
}

// Maps each threshold key to the feature.properties keys that can supply its value, in priority order
const THRESHOLD_PROPERTY_MAP: Record<string, string[]> = {
  ceilingHeight:  ['CL61Ceiling', 'ceiling', 'Ceiling'],
  precipitation: ['PrecipRate'],
  totalPrecipitation: ['TotalPrecipitation'],
  visibility: ['Visibility','visibility'],
  surfaceWind: [
    'WindSpeed',
    'PeakWindSpeed',
    'Wind Speed (max)',
    'WindSpeedMax',
    'WindSpeedMin',
    'wind_speed',
    'TowerWindSpeed',
    'TowerWindGust',
    'TowerPeakWindGust',
    'PeakWindGust',
  ],
  wind80M: ['WindSpeed80M', 'Wind80M'],
  surfaceWindGust: ['WindGust'],
  temperatureHighRange: ['Temperature',
    'TemperatureMax',
    'TemperatureMin',
    'temperature',
    'TowerTemperature',
    'TowerMaxAirTemp',
    'TowerMinAirTemp',
    'WindChillTemperature',
    'MaxAirTemp',
    'MinAirTemp'
  ],
  temperatureLowRange: [
    'Temperature',
    'TemperatureMax',
    'TemperatureMin',
    'temperature',
    'TowerTemperature',
    'TowerMaxAirTemp',
    'TowerMinAirTemp',
    'WindChillTemperature',
    'MaxAirTemp',
    'MinAirTemp'
  ],
}

const getPropertyValue = (
  properties: Record<string, any> | undefined,
  propertyNames: string[]
): number | null => {
  if (!properties) return null
  for (const name of propertyNames) {
    const value = properties[name]?.value
    if (typeof value === 'number') {
      return value
    }
  }
  return null
}

interface CustomMapMarkerProps {
  index: number
  feature: NetworkData
  tableView: boolean
  popupContent?: React.ReactElement | null
  networkData: NetworkData[]
}

const alertIcon = alertMarkerIcon('#FF5F15')
const eventIcon = sensorMarkerIcon('#248cc5')
const greenIcon = sensorMarkerIcon('green')
const yellowIcon = sensorMarkerIcon('yellow')
const redIcon = sensorMarkerIcon('red')

const CustomMapMarker = ({
  index,
  feature,
  tableView,
  popupContent,
  networkData,
}: CustomMapMarkerProps) => {
  const darkMode = useSelector((state: RootState) => state.mapState.darkMode)
  const { activeEventSensorId, setActiveEventsSensors } = useSensorEventContext()
  const currThresholdUnit = useSelector((state: RootState) => state.user.thresholdUnits)

  const getSensorIcon = (feature: NetworkData) => {
    let sensorIdentifier =
      feature.properties?.SensorType?.value +
      '-' +
      feature.properties?.StationID?.value
    if (activeEventSensorId.hasOwnProperty(sensorIdentifier)) {
      return activeEventSensorId[sensorIdentifier]['activeAlert']
        ? alertIcon
        : eventIcon
    }
    return markerColor(feature, currThresholdUnit)
  }
  const getSensorEventId = (feature: NetworkData) => {
    let sensorIdentifier =
      feature.properties?.SensorType?.value +
      '-' +
      feature.properties?.StationID?.value
    if (activeEventSensorId.hasOwnProperty(sensorIdentifier)) {
      return activeEventSensorId[sensorIdentifier]['eventId']
    }
    return null
  }
  let markerComponent
  const giarmetTypes = ['TANGO', 'SIERRA', 'ZULU']

  if (feature.properties === undefined) {
    console.log('feature undefined', feature)
  } else if (
    Object.keys(feature.properties).includes('Raw TAF') ||
    feature.properties['Report Type']?.value == 'METAR' ||
    feature.properties?.data == 'AIREP' ||
    feature.properties.airepType
  ) {
    Object.keys(feature.properties).includes('Raw TAF') ||
    Object.keys(feature.properties).includes('rawOb')
      ? (markerComponent = (
          <TAFMarker
            feature={feature}
            darkMode={darkMode}
            tableView={tableView}
            popupContent={popupContent}
            pirep={Object.keys(feature.properties).includes('rawOb')}
          />
        ))
      : feature.properties['Report Type']?.value == 'METAR'
      ? (markerComponent = (
          <METARMarker
            feature={feature}
            darkMode={darkMode}
            tableView={tableView}
            popupContent={popupContent}
          />
        ))
      : null
  } else if (giarmetTypes.includes(feature.properties?.product)) {
    markerComponent = (
      <AirmetPolygon
        feature={feature}
        darkMode={darkMode}
        tableView={tableView}
        popupContent={popupContent}
        networkData={networkData}
      />
    )
  } else if (feature.properties?.airSigmetType == 'SIGMET') {
    markerComponent = (
      <AirmetPolygon
        feature={feature}
        darkMode={darkMode}
        tableView={tableView}
        popupContent={popupContent}
        networkData={networkData}
      />
    )
  } else if (
    feature.properties?.StationID ||
    feature.properties?.SensorType?.value
  ) {
    if (
      feature.properties?.SensorType?.value == 'NRG' ||
      feature.properties?.SensorType?.value == 'Meteodrone'
    ) {
      markerComponent = (
        <Marker
          key={index}
          position={[feature.latitude, feature.longitude]}
          icon={getSensorIcon(feature)}
        >
          <Popup
            className="custom-popup"
            autoClose={false}
            key={feature.properties.id}
          >
            {tableView ? (
              <MapPopupTable properties={feature.properties} />
            ) : (
              { popupContent }
            )}
          </Popup>
        </Marker>
      )
    } else if (feature.properties?.SensorType?.value == 'CL61' || feature.properties?.SensorType?.value == 'CL31') {
      markerComponent = (
        <Marker
          key={index}
          position={[feature.latitude, feature.longitude]}
          icon={getSensorIcon(feature)}
        >
          <Popup
            className="lidar-popup"
            autoClose={false}
            key={feature.properties.id}
          >
            {tableView ? (
              <MapPopupTable properties={feature.properties} />
            ) : (
              { popupContent }
            )}
          </Popup>
        </Marker>
      )
    } else {
      markerComponent = (
        <Marker
          key={index}
          position={[feature.latitude, feature.longitude]}
          icon={getSensorIcon(feature)}
        >
          <Popup
            className="custom-popup"
            autoClose={false}
            key={feature.properties.id}
          >
            {tableView ? (
              <MapPopupTable
                properties={feature.properties}
                latitude={feature.latitude}
                longitude={feature.longitude}
                alertFeature={true}
                activeEvent={getSensorEventId(feature)}
              />
            ) : (
              { popupContent }
            )}
          </Popup>
        </Marker>
      )
    }
  } else if (
    feature.properties?.messageType &&
    (feature?.properties?.messageType == 'ALERT' ||
      feature?.properties?.messageType == 'CONTINUATION')
  ) {
  if (feature.type == 'Point') {
      markerComponent = (
        <Marker
          zIndex={2999999999}
          key={index}
          position={[feature.coordinates[1], feature.coordinates[0]]}
          isAlert={true}
          icon={alertIcon}
        >
          <Popup className="p-0 custom-popup" key={feature.properties.id}>
            <AlertsPopup alertsData={feature} />
          </Popup>
        </Marker>
      )
    }
    // Poly Line
    else {
      markerComponent = (
        <Polyline
          key={index}
          fill={markerFillAlert(feature)}
          fillOpacity={markerFillOpacity(feature)}
          weight={2.25}
          dashArray={markerDashAlert(feature)}
          fillColor={markerColorAlert(feature)}
          color={markerColorAlert(feature)}
          positions={[feature.coordinates]}
        >
          <Popup
            className="p-0 custom-popup"
            autoClose={false}
            key={feature.properties.id}
          >
            <PolygonPopupTable
              networkData={networkData}
              popupContent={popupContent}
              tableView={tableView}
              feature={feature}
            ></PolygonPopupTable>
          </Popup>
        </Polyline>
      )
    }
  } else if (
    feature?.properties?.type &&
    feature?.properties?.type == 'Event'
  ) {
    markerComponent = (
      <Polygon
        key={index}
        fill={true}
        weight={Number(2)}
        positions={[feature.bufferGeometry]}
        pathOptions={{
          color: feature.properties?.activeModelAlert
            ? '#FF5F1570'
            : '#248dc570',
        }}
      >
        <Popup
          className="p-0 custom-popup"
          autoClose={false}
          key={feature.properties.id}
        >
          <EventBox properties={feature.properties} />
        </Popup>
      </Polygon>
    )
  } else {
    return null
  }
  return markerComponent
}

const markerColor = (feature: NetworkData, thresholdAndUnit: WeatherThresholdConfig | undefined) => {
  if (!thresholdAndUnit?.thresholds) {
    return greenIcon
  }

  let hasMarginalRisk = false

  for (const [thresholdKey, propertyNames] of Object.entries(THRESHOLD_PROPERTY_MAP)) {
    const threshold = thresholdAndUnit.thresholds[thresholdKey]
    if (!threshold) continue

    const value = getPropertyValue(feature.properties, propertyNames)
    if (value === null) continue

    if (evaluateComparator(value, threshold.comparator, threshold.currentHighRisk)) {
      return redIcon
    }
    if (evaluateComparator(value, threshold.comparator, threshold.currentMarginalRisk)) {
      hasMarginalRisk = true
    }
  }

  return hasMarginalRisk ? yellowIcon : greenIcon
}
const markerColorAlert = (feature: NetworkData) => {
  let color = '#'
  elements.forEach((element) => {
    if (feature?.properties?.hazardType === element?.value) {
      color += element?.color
      return
    }
  })
  return color
}
const markerFillAlert = (feature: NetworkData) => {
  let fill = false
  elements.forEach((element) => {
    if (feature?.properties?.hazardType === element?.value) {
      fill = element?.fill
      return
    }
  })
  return fill
}
const markerFillOpacity = (feature: NetworkData) => {
  let opacity = 0.3
  elements.forEach((element) => {
    if (feature?.properties?.hazardType === element?.value) {
      opacity = element?.opacity
      return
    }
  })
  return opacity
}
const markerDashAlert = (feature: NetworkData) => {
  let dash
  elements.forEach((element) => {
    if (feature?.properties?.hazardType === element?.value) {
      if (element?.line === 'dashed') {
        dash = '10'
        return
      } else if (element?.line === 'dot') {
        dash = '3'
        return
      } else {
        dash = '0'
        return
      }
    }
  })
  return dash
}
export default CustomMapMarker
